/**
 * Dev-mode controls — only renders when the backend reports
 * ``app_env=='development'``. In production builds the API call returns
 * `{enabled:false}` and the panel never mounts, so production users
 * never see this UI.
 *
 * Two subcomponents:
 *  - DevLoginForm: shown on /login when no third-party OAuth is set up
 *  - DevPremiumToggle: shown on /account so you can toggle Premium
 *    state without going through NewebPay
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth, api, formatApiError } from '../api';
import { useAuth } from '../auth';

let devEnabledCache: boolean | null = null;

/** Hook that resolves once after first call; cached for the page session. */
export function useDevEnabled(): boolean {
  const [enabled, setEnabled] = useState<boolean>(devEnabledCache ?? false);
  useEffect(() => {
    if (devEnabledCache !== null) return;
    void api.devStatus().then((r) => {
      devEnabledCache = r.enabled;
      setEnabled(r.enabled);
    });
  }, []);
  return enabled;
}

export function DevLoginForm({
  onBeforeSubmit,
}: {
  // Compliance Baseline (STA-128, STA-132) — Login.tsx passes a gate
  // here so the dev shortcut also requires the consent checkbox.
  onBeforeSubmit?: () => boolean;
} = {}) {
  const enabled = useDevEnabled();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState('test@example.com');
  const [name, setName] = useState('Test User');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!enabled) return null;

  const submit = async () => {
    if (onBeforeSubmit && !onBeforeSubmit()) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.devLogin(email, name);
      Auth.setToken(r.access_token);
      // Seed minimal user; refresh() pulls full state next.
      // RC-P2 — onboarding_complete defaults to false so ProtectedRoute
      // doesn't briefly think a fresh dev login is fully onboarded
      // before refresh() lands the real value. Same defensive defaults
      // for nickname/referral_code so Layout / nav don't choke on
      // ``user.referral_code`` (Friends invite UI) during the gap.
      Auth.setUser({
        id: r.user.id,
        email: r.user.email,
        display_name: r.user.display_name,
        avatar_url: r.user.avatar_url,
        stardust: r.user.stardust,
        subscription: { status: 'none', provider: null, current_period_end: null, cancels_at: null },
        daily_readings_used: null,
        daily_readings_limit: null,
        readings_reset_at: null,
        onboarding_complete: false,
        nickname: null,
        referral_code: null,
      });
      // Best-effort: replay the consent flag for dev logins too so the
      // DB row is consistent with what production users produce. Needs
      // the bearer token set above to authenticate.
      try {
        await api.submitConsent({
          is_adult: true,
          tos_agreed: true,
          privacy_policy_agreed: true,
        });
      } catch {
        // non-fatal in dev
      }
      await refresh();
      nav('/reading', { replace: true });
    } catch (e) {
      setError(formatApiError(e, 'Dev login 失敗，請稍後再試。'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-10 rounded-xl border border-yellow-500/40 bg-yellow-500/5 p-4">
      <div className="text-yellow-400 text-xs font-medium mb-3">
        ⚙ DEV MODE — 不需要 OAuth 設定即可登入
      </div>
      <div className="space-y-2">
        <input
          className="input text-sm"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email"
        />
        <input
          className="input text-sm"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="顯示名稱"
        />
        <button
          onClick={submit}
          disabled={loading}
          className="btn-ghost w-full text-sm"
        >
          {loading ? '登入中…' : 'Dev login (skip OAuth)'}
        </button>
        {error && <p className="text-red-400 text-xs">{error}</p>}
      </div>
      <p className="mt-3 text-[11px] text-yellow-400/70">
        production 部署不會看到這個區塊（後端 app_env != development 時消失）。
      </p>
    </div>
  );
}

export function DevPremiumToggle() {
  const enabled = useDevEnabled();
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  if (!enabled || !user) return null;

  const grant = async () => {
    setLoading(true);
    setMsg(null);
    try {
      const r = await api.devGrantPremium(30);
      setMsg(
        `已強制開通至 ${r.subscription_period_end ? new Date(r.subscription_period_end).toLocaleDateString('zh-TW') : '未知日期'}`,
      );
      await refresh();
    } catch (e) {
      setMsg(`失敗：${formatApiError(e, '請稍後再試。')}`);
    } finally {
      setLoading(false);
    }
  };

  const revoke = async () => {
    setLoading(true);
    setMsg(null);
    try {
      await api.devRevokePremium();
      setMsg('已撤銷 Premium，回到免費版');
      await refresh();
    } catch (e) {
      setMsg(`失敗：${formatApiError(e, '請稍後再試。')}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card border-yellow-500/40 bg-yellow-500/5 mt-6">
      <div className="text-yellow-400 text-xs font-medium mb-3">
        ⚙ DEV MODE — 不用金流測試訂閱狀態
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={grant} disabled={loading} className="btn-ghost text-sm">
          強制開通 30 天 Premium
        </button>
        <button onClick={revoke} disabled={loading} className="btn-ghost text-sm">
          撤銷 Premium
        </button>
      </div>
      {msg && <p className="mt-3 text-xs text-tarot-muted">{msg}</p>}
      <p className="mt-3 text-[11px] text-yellow-400/70">
        ``billing_provider='dev'`` 標記。production 部署不會看到。
      </p>
    </div>
  );
}
